import {
    JBI_UI_REGION,
    type CurriculumItemCtaCardProps,
    type JbiCourseData,
    type JBIAISummaryProps,
    type JBIVideoCardProps,
    type JbiCurrentSection,
    type JbiLearningProgressCardTrackingProps,
} from '@udemy/react-my-learning-unit';

import {JbiEnhancedLectureVariant} from 'src/lib/experimentation-constants';
import {JbiCourseDetailsData, NextCurriculumItem, UserEnrollmentsData} from 'src/types/page-props';

type CourseEnrollment = NonNullable<
    NonNullable<
        NonNullable<UserEnrollmentsData['me']['enrollments']>['courseEnrollmentsPaged']
    >['courseEnrollments'][number]
>;

export type CourseEnrollmentWithNextItem = CourseEnrollment & {
    course: CourseEnrollment['course'] & {
        nextCurriculumItem: NextCurriculumItem;
    };
};

type CourseContext = Pick<CourseEnrollment['course'], 'urlCourseTaking' | 'images'>;

type CourseContextForCtaCard = Pick<
    CourseEnrollment['course'],
    'title' | 'headline' | 'urlCourseTaking' | 'images'
>;

type VideoNextItem = Extract<
    NextCurriculumItem,
    {__typename: 'VideoLecture'} | {__typename: 'VideoMashupLecture'}
>;

type VideoLectureNextItem = Extract<NextCurriculumItem, {__typename: 'VideoLecture'}>;

type CourseContextForSummary = Pick<
    CourseEnrollment['course'],
    'title' | 'urlCourseTaking' | 'images'
>;

type GraphQLMediaSource = NonNullable<NonNullable<VideoNextItem['asset']>['mediaSources']>[number];

const MEDIA_TYPE_TO_MIME: Record<string, string> = {
    APPLICATION_X_MPEGURL: 'application/x-mpegURL',
    APPLICATION_DASH_XML: 'application/dash+xml',
    VIDEO_MP4: 'video/mp4',
    AUDIO_MP3: 'audio/mpeg',
};

/** Returns true when the next curriculum item is a video lecture. */
export function isNextItemVideoLecture(nextItem: NextCurriculumItem): nextItem is VideoNextItem {
    return nextItem.__typename === 'VideoLecture' || nextItem.__typename === 'VideoMashupLecture';
}

/** Narrows a filtered course enrollment to one with a defined next curriculum item. */
export function asEnrollmentWithNextItem(
    enrollment: CourseEnrollment | null | undefined,
): CourseEnrollmentWithNextItem | null {
    const nextItem = enrollment?.course?.nextCurriculumItem;

    if (!enrollment || !nextItem) {
        return null;
    }

    return {
        ...enrollment,
        course: {
            ...enrollment.course,
            nextCurriculumItem: nextItem,
        },
    };
}

function getNextItemUrl(nextItem: NextCurriculumItem, courseUrl?: string | null): string {
    if ('urlLanding' in nextItem && nextItem.urlLanding) {
        return nextItem.urlLanding;
    }

    return courseUrl ?? '/';
}

function getNextItemDuration(nextItem: NextCurriculumItem): number {
    return 'durationInSeconds' in nextItem ? nextItem.durationInSeconds ?? 0 : 0;
}

function getNextItemThumbnail(
    nextItem: NextCurriculumItem,
    courseImage?: string | null,
): string | undefined {
    const itemThumbnail = 'thumbnail' in nextItem ? nextItem.thumbnail : undefined;

    return itemThumbnail || courseImage || undefined;
}

function getLastWatchedSecond(nextItem: NextCurriculumItem): number {
    return 'lastWatchedSecond' in nextItem ? nextItem.lastWatchedSecond ?? 0 : 0;
}

function mapMediaSources(
    mediaSources?: GraphQLMediaSource[] | null,
): JBIVideoCardProps['mediaSources'] {
    if (!mediaSources?.length) {
        return [];
    }

    return mediaSources.map(({src, mediaType, label}) => ({
        src,
        type: MEDIA_TYPE_TO_MIME[mediaType] ?? mediaType,
        label,
    }));
}

/** Maps GraphQL enrollment data to CurriculumItemCtaCard props. */
export function toCurriculumItemCtaCardProps(
    course: CourseContextForCtaCard,
    nextItem: NextCurriculumItem,
    courseData?: JbiCourseData,
): CurriculumItemCtaCardProps {
    const durationInSeconds = getNextItemDuration(nextItem);
    const thumbnailUrl = getNextItemThumbnail(nextItem, course.images?.px240x135);

    return {
        typename: nextItem.__typename,
        itemTitle: nextItem.title,
        itemUrl: getNextItemUrl(nextItem, course.urlCourseTaking),
        courseTitle: course.title,
        ...(durationInSeconds > 0 ? {durationInSeconds} : {}),
        ...(course.headline ? {description: course.headline} : {}),
        ...(thumbnailUrl ? {thumbnailUrl} : {}),
        ...(courseData?.progress ? {progress: courseData.progress} : {}),
        ...(courseData?.currentSection ? {currentSection: courseData.currentSection} : {}),
        ...(courseData?.learningOutcomes ? {learningOutcomes: courseData.learningOutcomes} : {}),
        ...(courseData?.rating ? {rating: courseData.rating} : {}),
        ...(courseData?.enrollmentCount ? {enrollmentCount: courseData.enrollmentCount} : {}),
        ...(courseData?.instructor ? {instructor: courseData.instructor} : {}),
    };
}

function toLearningProgressCardTrackingProps(
    courseTrackingId: string,
    nextItem: NextCurriculumItem,
    uiRegion: string,
): JbiLearningProgressCardTrackingProps {
    return {
        courseTrackingId,
        curriculumId: Number.parseInt(nextItem.id, 10),
        uiRegion,
    };
}

/** Maps GraphQL enrollment data to JBIVideoCard props. */
export function toJBIVideoCardProps(
    course: CourseContext,
    nextItem: VideoNextItem,
    courseData?: JbiCourseData,
): JBIVideoCardProps {
    const asset = nextItem.asset ?? undefined;

    return {
        lectureTitle: nextItem.title,
        lectureUrl: getNextItemUrl(nextItem, course.urlCourseTaking),
        startTime: getLastWatchedSecond(nextItem),
        duration: getNextItemDuration(nextItem),
        assetId: Number.parseInt(asset?.id ?? nextItem.id, 10),
        thumbnailUrl: getNextItemThumbnail(nextItem, course.images?.px240x135),
        mediaSources: mapMediaSources(asset?.mediaSources),
        mediaLicenseToken: asset?.accessToken ?? undefined,
        ...(courseData?.progress ? {progress: courseData.progress} : {}),
        ...(courseData?.learningOutcomes ? {learningOutcomes: courseData.learningOutcomes} : {}),
        ...(courseData?.rating ? {rating: courseData.rating} : {}),
        ...(courseData?.instructor ? {instructor: courseData.instructor} : {}),
    };
}

export const toJBIAISummaryProps = (
    course: CourseContextForSummary,
    nextItem: VideoLectureNextItem,
    courseData?: JbiCourseData,
): JBIAISummaryProps => ({
    course: {
        title: course.title,
        urlCourseTaking: course.urlCourseTaking ?? undefined,
        images: course.images as unknown as JBIAISummaryProps['course']['images'],
    },
    ...(nextItem.aiSummary ? {summary: {text: nextItem.aiSummary}} : {}),
    ...(courseData?.progress ? {progress: courseData.progress} : {}),
    ...(courseData?.rating ? {rating: courseData.rating} : {}),
    ...(courseData?.instructor ? {instructor: courseData.instructor} : {}),
});

type CourseDetailsSections = NonNullable<JbiCourseDetailsData>['curriculum']['sections'];

interface SectionPosition {
    sectionIndex: number;
    sectionCount: number;
    currentSection: JbiCurrentSection;
    sectionDescription?: string;
}

/**
 * Finds the title of the curriculum item that comes immediately after the item at
 * `itemIndex` within `section.items`, falling back to the first item of the next section
 * (`nextSection`) when the matched item is the last one in its own section.
 */
function findUpcomingItemTitle(
    sections: CourseDetailsSections,
    sectionIndex: number,
    itemIndex: number,
): string | undefined {
    const section = sections[sectionIndex];
    const upcomingInSameSection = section.items[itemIndex + 1];

    if (upcomingInSameSection) {
        return upcomingInSameSection.title;
    }

    const nextSection = sections[sectionIndex + 1];

    return nextSection?.items[0]?.title;
}

/**
 * Locates the next curriculum item within the course's curriculum sections to derive
 * "Section X of Y" and the current-section card. Returns undefined when the item can't be
 * found (versioning skew between EnrollmentService and CurriculumService, draft items) so
 * callers can hide all section UI rather than show a wrong count.
 */
export function deriveSectionPosition(
    sections: CourseDetailsSections | undefined | null,
    nextItemId: string,
): SectionPosition | undefined {
    if (!sections?.length) {
        return undefined;
    }

    const sectionCount = sections.length;

    for (let index = 0; index < sections.length; index++) {
        const section = sections[index];

        if (section.id === nextItemId) {
            return {
                sectionIndex: index + 1,
                sectionCount,
                currentSection: {
                    title: section.title,
                    ...(section.items[0] ? {nextItemTitle: section.items[0].title} : {}),
                },
                ...(section.description ? {sectionDescription: section.description} : {}),
            };
        }

        const itemIndex = section.items.findIndex((item) => item.id === nextItemId);

        if (itemIndex !== -1) {
            const upcomingItemTitle = findUpcomingItemTitle(sections, index, itemIndex);

            return {
                sectionIndex: index + 1,
                sectionCount,
                currentSection: {
                    title: section.title,
                    ...(upcomingItemTitle ? {nextItemTitle: upcomingItemTitle} : {}),
                },
                ...(section.description ? {sectionDescription: section.description} : {}),
            };
        }
    }

    return undefined;
}

/** Assembles the shared card data from an enrollment plus the JbiCourseDetails query result. */
export function toJbiCourseData(
    enrollment: CourseEnrollmentWithNextItem,
    courseDetails: JbiCourseDetailsData,
): JbiCourseData {
    const {course, completionPercentage} = enrollment;
    const sectionPosition = courseDetails
        ? deriveSectionPosition(courseDetails.curriculum.sections, course.nextCurriculumItem.id)
        : undefined;

    const rating = course.rating?.count
        ? {average: course.rating.average ?? 0, count: course.rating.count}
        : undefined;

    const firstInstructor = course.instructors[0];
    const instructor = firstInstructor
        ? {
              name: firstInstructor.name,
              ...(firstInstructor.url ? {url: firstInstructor.url} : {}),
              ...(firstInstructor.images?.px50x50
                  ? {imageUrl: firstInstructor.images.px50x50}
                  : {}),
          }
        : undefined;

    const enrollmentCount =
        courseDetails?.enrollments.count ?? courseDetails?.enrollments.roundedCount;

    // Prefer the current section's own description (module-specific) over the course-wide
    // learningOutcomes list, since that's what the "in this module" heading actually promises.
    // Falls back to course outcomes since many sections leave their description empty.
    const learningOutcomes = sectionPosition?.sectionDescription
        ? [sectionPosition.sectionDescription]
        : courseDetails?.learningOutcomes.length
          ? courseDetails.learningOutcomes
          : undefined;

    return {
        progress: {
            completionPercentage: completionPercentage ?? 0,
            ...(sectionPosition
                ? {
                      sectionIndex: sectionPosition.sectionIndex,
                      sectionCount: sectionPosition.sectionCount,
                  }
                : {}),
        },
        ...(rating ? {rating} : {}),
        ...(enrollmentCount ? {enrollmentCount} : {}),
        ...(learningOutcomes ? {learningOutcomes} : {}),
        ...(instructor ? {instructor} : {}),
        ...(sectionPosition?.currentSection
            ? {currentSection: sectionPosition.currentSection}
            : {}),
    };
}

export type TrackedJbiCardConfig =
    | {
          type: 'video';
          cardProps: JBIVideoCardProps;
          trackingProps: JbiLearningProgressCardTrackingProps;
      }
    | {
          type: 'curriculum';
          cardProps: CurriculumItemCtaCardProps;
          trackingProps: JbiLearningProgressCardTrackingProps;
      }
    | {
          type: 'summary';
          cardProps: JBIAISummaryProps;
      };

const toCurriculumCardConfig = (
    course: CourseContextForCtaCard,
    nextItem: NextCurriculumItem,
    generateId: () => string,
    courseData?: JbiCourseData,
) => ({
    type: 'curriculum' as const,
    cardProps: toCurriculumItemCtaCardProps(course, nextItem, courseData),
    trackingProps: toLearningProgressCardTrackingProps(
        generateId(),
        nextItem,
        JBI_UI_REGION.CURRICULUM,
    ),
});

interface ResolveTrackedJbiCardParams {
    isLoading: boolean;
    variant: JbiEnhancedLectureVariant | undefined;
    enrollment: CourseEnrollmentWithNextItem | null;
    generateId: () => string;
    courseDetails?: JbiCourseDetailsData;
}

/** Resolves which tracked JBI card to render for the current experiment variant. */
export function resolveTrackedJbiCard({
    isLoading,
    variant,
    enrollment,
    generateId,
    courseDetails,
}: ResolveTrackedJbiCardParams): TrackedJbiCardConfig | null {
    if (isLoading || !enrollment) return null;
    if (variant === 'control') return null;

    const {course} = enrollment;
    const nextItem = course.nextCurriculumItem;
    const courseData = toJbiCourseData(enrollment, courseDetails ?? null);

    if (variant === 'summary') {
        if (nextItem.__typename === 'VideoLecture') {
            return {
                type: 'summary',
                cardProps: toJBIAISummaryProps(course, nextItem, courseData),
            };
        }

        return toCurriculumCardConfig(course, nextItem, generateId, courseData);
    }

    if (variant === 'video' && isNextItemVideoLecture(nextItem)) {
        return {
            type: 'video',
            cardProps: toJBIVideoCardProps(course, nextItem, courseData),
            trackingProps: toLearningProgressCardTrackingProps(
                generateId(),
                nextItem,
                JBI_UI_REGION.VIDEO,
            ),
        };
    }

    if (variant === 'curriculum' || variant === 'video') {
        return toCurriculumCardConfig(course, nextItem, generateId, courseData);
    }

    return null;
}
